import type { CountryReference, CountryReferenceBundle } from '../services/platform/masterService';
import { validateOptionalDescription } from './descriptionValidation';

export type CountryMeta = Record<string, string | number | boolean>;

export interface CountryFormValues {
  code: string;
  name: string;
  description: string;
  sortOrder: number;
  metadata: CountryMeta;
}

export interface CountryExistingRecord {
  _id?: string;
  code?: string;
  name?: string;
  sortOrder?: number;
  metadata?: Record<string, unknown>;
}

export const COUNTRY_FIELD_LABELS: Record<string, string> = {
  country: 'Country',
  name: 'Display name',
  code: 'Code',
  description: 'Description',
  sortOrder: 'Sort order',
  iso3Code: 'ISO3 Code',
  phoneCode: 'Phone Code',
  phoneLength: 'Phone Length',
  nationality: 'Nationality Label',
  flagUrl: 'Flag',
  defaultCurrency: 'Default Currency',
  secondaryCurrency: 'Secondary Currency',
  defaultLanguage: 'Default Language',
  additionalLanguages: 'Additional Languages',
  timezone: 'Default Timezone',
  weekendDays: 'Weekend Days',
  firstDayOfWeek: 'First Day of Week',
  dateFormat: 'Date Format',
  timeFormat: 'Time Format',
  numberFormat: 'Number Format',
  taxSystem: 'Tax System',
  gstVatEnabled: 'GST/VAT Enabled',
  invoicePrefix: 'Invoice Prefix',
  postalCodePattern: 'Postal Code Format',
  addressFormat: 'Address Format',
  measurementSystem: 'Measurement System',
  distanceUnit: 'Distance Unit',
  weightUnit: 'Weight Unit',
  temperatureUnit: 'Temperature Unit',
  gpsFormat: 'GPS Coordinate Format',
  emergencyNumbers: 'Emergency Numbers',
};

export const UNITS_BY_SYSTEM = {
  metric: { distanceUnit: ['km'], weightUnit: ['kg'], temperatureUnit: ['celsius'] },
  imperial: { distanceUnit: ['mi'], weightUnit: ['lb'], temperatureUnit: ['fahrenheit', 'celsius'] },
} as const;

const NAME_PATTERN = /^[\p{L}][\p{L}\p{M} .,'()&-]*$/u;
const PHONE_CODE_PATTERN = /^\+\d{1,4}(-\d{1,4})?$/;
const PHONE_LENGTH_PATTERN = /^(\d{1,2})(-(\d{1,2}))?$/;
const INVOICE_PREFIX_PATTERN = /^[A-Z][A-Z0-9-]{1,9}$/;
const NATIONALITY_PATTERN = /^[\p{L}][\p{L} '-]{1,49}$/u;
const EMERGENCY_NUMBER_PATTERN = /^\d{2,6}$/;

const str = (v: unknown) => (v == null ? '' : String(v).trim());

export function parseJsonArray<T = unknown>(value: unknown): T[] | null {
  if (Array.isArray(value)) return value as T[];
  const raw = str(value);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : null;
  } catch {
    return null;
  }
}

export function parseJsonObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  const raw = str(value);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function isValidTimezone(tz: string) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function nextCountrySortOrder(records: CountryExistingRecord[], excludeId?: string) {
  return records
    .filter((r) => r._id !== excludeId)
    .reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0) + 1;
}

/** Field-level errors for the Countries master form (mirrors backend countryMasterValidation). */
export function validateCountryForm(
  form: CountryFormValues,
  ctx: {
    bundle?: CountryReferenceBundle;
    reference?: CountryReference;
    existing: CountryExistingRecord[];
    editingId?: string;
  },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const meta = form.metadata;
  const others = ctx.existing.filter((r) => r._id !== ctx.editingId);
  const code = str(form.code).toUpperCase();
  const name = str(form.name);

  if (!code) errors.country = 'Select a country';
  else if (!/^[A-Z]{2}$/.test(code)) errors.code = 'Code must be a 2-letter ISO code (e.g. IN)';
  else {
    const taken = others.find((r) => str(r.code).toUpperCase() === code);
    if (taken) errors.country = `${taken.name} (${code}) already exists in the Countries master`;
  }

  if (!name) errors.name = 'Display name is required';
  else if (name.length < 2 || name.length > 60) errors.name = 'Display name must be 2–60 characters';
  else if (!NAME_PATTERN.test(name)) errors.name = 'Use letters, spaces and . , \' ( ) & - only';
  else {
    const taken = others.find((r) => str(r.name).toLowerCase() === name.toLowerCase());
    if (taken) errors.name = `"${name}" is already used by ${taken.code}`;
  }

  const description = str(form.description);
  if (description.length > 500) errors.description = 'Description must be 500 characters or fewer';
  else {
    const descErr = validateOptionalDescription(description);
    if (descErr) errors.description = descErr;
  }

  const order = Number(form.sortOrder);
  if (!Number.isInteger(order) || order < 1 || order > 9999) {
    errors.sortOrder = 'Sort order must be a whole number from 1 to 9999';
  } else {
    const taken = others.find((r) => Number(r.sortOrder) === order);
    if (taken) errors.sortOrder = `Sort order ${order} is already used by ${taken.name} — next free is ${nextCountrySortOrder(ctx.existing, ctx.editingId)}`;
  }

  const ref = ctx.reference;
  const iso3 = str(meta.iso3Code).toUpperCase();
  if (!iso3) errors.iso3Code = 'ISO3 Code is required';
  else if (!/^[A-Z]{3}$/.test(iso3)) errors.iso3Code = 'ISO3 Code must be exactly 3 letters (e.g. IND)';
  else if (ref && ref.iso3 !== iso3) errors.iso3Code = `ISO3 Code for ${ref.name} is ${ref.iso3}`;
  else {
    const taken = others.find((r) => str(r.metadata?.iso3Code).toUpperCase() === iso3);
    if (taken) errors.iso3Code = `ISO3 Code ${iso3} is already used by ${taken.name}`;
  }

  const phoneCode = str(meta.phoneCode);
  if (!phoneCode) errors.phoneCode = 'Phone Code is required';
  else if (!PHONE_CODE_PATTERN.test(phoneCode)) errors.phoneCode = 'Use + followed by 1–4 digits (e.g. +91)';

  const phoneLength = str(meta.phoneLength);
  if (phoneLength) {
    const m = PHONE_LENGTH_PATTERN.exec(phoneLength);
    const min = m ? Number(m[1]) : NaN;
    const max = m?.[3] ? Number(m[3]) : min;
    if (!m || min < 4 || max > 15 || min > max) {
      errors.phoneLength = 'Use digits 4–15, or a range like 10-11';
    }
  }

  const nationality = str(meta.nationality);
  if (nationality && !NATIONALITY_PATTERN.test(nationality)) {
    errors.nationality = 'Use 2–50 letters (spaces, hyphens and apostrophes allowed)';
  }

  const currencies = new Set(ctx.bundle?.currencies || []);
  const defaultCurrency = str(meta.defaultCurrency).toUpperCase();
  if (!defaultCurrency) errors.defaultCurrency = 'Default Currency is required';
  else if (currencies.size && !currencies.has(defaultCurrency)) errors.defaultCurrency = 'Select a valid ISO currency';
  const secondaryCurrency = str(meta.secondaryCurrency).toUpperCase();
  if (secondaryCurrency) {
    if (currencies.size && !currencies.has(secondaryCurrency)) errors.secondaryCurrency = 'Select a valid ISO currency';
    else if (secondaryCurrency === defaultCurrency) errors.secondaryCurrency = 'Must be different from Default Currency';
  }

  const languageCodes = new Set((ctx.bundle?.languages || []).map((l) => l.code));
  const defaultLanguage = str(meta.defaultLanguage).toLowerCase();
  if (!defaultLanguage) errors.defaultLanguage = 'Default Language is required';
  else if (languageCodes.size && !languageCodes.has(defaultLanguage)) errors.defaultLanguage = 'Select a valid language';
  const additional = parseJsonArray<string>(meta.additionalLanguages);
  if (additional === null) errors.additionalLanguages = 'Must be a JSON array of language codes';
  else if (additional.some((c) => typeof c !== 'string' || (languageCodes.size > 0 && !languageCodes.has(c.toLowerCase())))) {
    errors.additionalLanguages = 'Contains an invalid language code';
  } else if (additional.some((c) => c.toLowerCase() === defaultLanguage)) {
    errors.additionalLanguages = 'Must not repeat the Default Language';
  }

  const timezone = str(meta.timezone);
  if (!timezone) errors.timezone = 'Default Timezone is required';
  else if (!isValidTimezone(timezone)) errors.timezone = 'Not a valid IANA timezone';
  else if (ref?.timezones.length && !ref.timezones.includes(timezone)) errors.timezone = `Choose a timezone used in ${ref.name}`;

  const weekend = parseJsonArray<number>(meta.weekendDays);
  if (weekend === null || weekend.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
    errors.weekendDays = 'Weekend Days must be weekday numbers 0–6';
  } else if (weekend.length > 3) errors.weekendDays = 'Select at most 3 weekend days';

  for (const key of ['firstDayOfWeek', 'dateFormat', 'timeFormat', 'numberFormat'] as const) {
    if (!str(meta[key])) errors[key] = `${COUNTRY_FIELD_LABELS[key]} is required`;
  }

  const taxSystem = str(meta.taxSystem);
  if (!taxSystem) errors.taxSystem = 'Tax System is required';
  const gstVat = meta.gstVatEnabled === true || meta.gstVatEnabled === 'true';
  if (gstVat && taxSystem && taxSystem !== 'gst' && taxSystem !== 'vat') {
    errors.gstVatEnabled = 'GST/VAT can only be enabled for GST or VAT tax systems';
  }

  const invoicePrefix = str(meta.invoicePrefix).toUpperCase();
  if (invoicePrefix) {
    if (!INVOICE_PREFIX_PATTERN.test(invoicePrefix)) {
      errors.invoicePrefix = '2–10 capital letters, digits or hyphens, starting with a letter';
    } else {
      const taken = others.find((r) => str(r.metadata?.invoicePrefix).toUpperCase() === invoicePrefix);
      if (taken) errors.invoicePrefix = `Already used by ${taken.name}`;
    }
  }

  const postal = str(meta.postalCodePattern);
  if (postal) {
    try {
      new RegExp(postal);
    } catch {
      errors.postalCodePattern = 'Not a valid regular expression';
    }
  }

  const tokens = new Set(ctx.bundle?.addressFormatTokens || []);
  const address = parseJsonArray<string>(meta.addressFormat);
  if (address === null) errors.addressFormat = 'Address Format must be a JSON array';
  else if (address.length) {
    if (tokens.size && address.some((t) => !tokens.has(t))) errors.addressFormat = 'Contains an unsupported address field';
    else if (!address.includes('line1') || !address.includes('city')) errors.addressFormat = 'Must include Address line 1 and City';
  }

  const measurement = str(meta.measurementSystem) as keyof typeof UNITS_BY_SYSTEM | '';
  if (!measurement) errors.measurementSystem = 'Measurement System is required';
  else {
    const allowed = UNITS_BY_SYSTEM[measurement];
    for (const key of ['distanceUnit', 'weightUnit', 'temperatureUnit'] as const) {
      const value = str(meta[key]);
      if (!value) errors[key] = `${COUNTRY_FIELD_LABELS[key]} is required`;
      else if (allowed && !(allowed[key] as readonly string[]).includes(value)) {
        errors[key] = `Not compatible with the ${measurement} system`;
      }
    }
  }
  if (!str(meta.gpsFormat)) errors.gpsFormat = 'GPS Coordinate Format is required';

  const emergency = parseJsonObject(meta.emergencyNumbers);
  if (emergency === null) errors.emergencyNumbers = 'Emergency Numbers must be a JSON object';
  else {
    const keys = new Set(ctx.bundle?.emergencyServiceKeys || ['police', 'ambulance', 'fire']);
    for (const [service, number] of Object.entries(emergency)) {
      if (!keys.has(service)) errors.emergencyNumbers = `Unsupported service "${service}"`;
      else if (!EMERGENCY_NUMBER_PATTERN.test(str(number))) errors[`emergency.${service}`] = 'Use 2–6 digits';
    }
  }

  return errors;
}

/** Non-blocking hints when locale settings differ from what is typical for the selected country. */
export function countryConsistencyWarnings(meta: CountryMeta, ref?: CountryReference): string[] {
  if (!ref) return [];
  const warnings: string[] = [];
  const d = ref.defaults;
  const currency = str(meta.defaultCurrency).toUpperCase();
  if (currency && ref.currencies.length && !ref.currencies.includes(currency)) {
    warnings.push(`Default Currency ${currency} is not an official currency of ${ref.name} (${ref.currencies.join(', ')})`);
  }
  const language = str(meta.defaultLanguage).toLowerCase();
  if (language && ref.languages.length && !ref.languages.includes(language)) {
    warnings.push(`Default Language "${language}" is not an official language of ${ref.name}`);
  }
  if (str(meta.dateFormat) && str(meta.dateFormat) !== d.dateFormat) warnings.push(`${ref.name} typically uses ${d.dateFormat} dates`);
  if (str(meta.timeFormat) && str(meta.timeFormat) !== d.timeFormat) warnings.push(`${ref.name} typically uses the ${d.timeFormat === '12h' ? '12-hour' : '24-hour'} clock`);
  if (str(meta.numberFormat) && str(meta.numberFormat) !== d.numberFormat) warnings.push(`${ref.name} typically uses the ${d.numberFormat} number format`);
  if (str(meta.measurementSystem) && str(meta.measurementSystem) !== d.measurementSystem) warnings.push(`${ref.name} typically uses the ${d.measurementSystem} system`);
  if (str(meta.taxSystem) && str(meta.taxSystem) !== d.taxSystem) warnings.push(`${ref.name} typically uses ${d.taxSystem.replace('_', ' ').toUpperCase()} taxation`);
  const address = parseJsonArray<string>(meta.addressFormat) || [];
  if (address.includes('postalCode') && !d.addressFormat.includes('postalCode')) {
    warnings.push(`${ref.name} does not normally use postal codes in addresses`);
  }
  return warnings;
}
